<template>
  <LayoutLogin>
    <template v-slot:content>
      <LayoutLoginCard class="consentView-card">
        <div class="d-flex justify-center my-6">
          <Logo type="big" />
        </div>

        <div v-if="isLoading">
          <LoaderSpinner />
        </div>

        <div v-else-if="hasError">
          <p class="my-4 text-body-1 text-center error--text">
            {{ errorMessage }}
          </p>
        </div>

        <div v-else>
          <p class="my-4 text-h6 text-center">
            {{ clientName }}
          </p>

          <p class="my-4 text-body-1 text-center">
            {{ $t('auth.oauth.consent_description', { clientName }) }}
          </p>

          <p
            v-if="signedInAsLabel"
            class="my-4 text-body-2 text-center"
            data-testid="consentView-text-userInfo"
          >
            {{ signedInAsLabel }}
          </p>

          <div v-if="signedInAsLabel" class="d-flex justify-center">
            <Button
              color="blueGreyDarken"
              data-testid="consentView-button-logout"
              plain
              :disabled="isSubmitting"
              :text="$t('auth.oauth.not_you_log_out')"
              @click="handleLogout"
            />
          </div>

          <div class="d-flex justify-space-between mt-8">
            <Button
              color="blueGreyDarken"
              data-testid="consentView-button-deny"
              plain
              :disabled="isSubmitting"
              :text="$t('auth.oauth.deny')"
              @click="handleDeny"
            />

            <Button
              color="primary"
              data-testid="consentView-button-authorize"
              :disabled="isSubmitting"
              :loading="isSubmitting"
              :text="$t('auth.oauth.authorize')"
              @click="handleAuthorize"
            />
          </div>
        </div>
      </LayoutLoginCard>
    </template>
  </LayoutLogin>
</template>

<script>
import isEmpty from 'lodash/isEmpty'

import api from '@/api'
import UserAuthentication from '@/auth'

import LayoutLogin from '@/components/Core/Layout/LayoutLogin'
import LayoutLoginCard from '@/containers/Access/components/LayoutLoginCard.vue'
import Logo from '@/components/Core/Logo/Logo.vue'
import LoaderSpinner from '@/components/utils/LoaderSpinner.vue'
import Button from '@/components/UI/Button.vue'

export default {
  name: 'ConsentView',

  components: {
    Button,
    LayoutLogin,
    LayoutLoginCard,
    Logo,
    LoaderSpinner,
  },

  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      hasError: false,
      errorMessage: '',
      clientName: '',
      redirectUri: '',
      userName: '',
      userEmail: '',
    }
  },

  computed: {
    sessionId() {
      return this.$route.query.session
    },

    accessToken() {
      const auth = new UserAuthentication(window.localStorage)
      return auth.getAccessToken()
    },

    signedInAsLabel() {
      if (!this.userName || !this.userEmail) {
        return ''
      }

      return this.$t('auth.oauth.signed_in_as', {
        userName: this.userName,
        userEmail: this.userEmail,
      })
    },
  },

  async mounted() {
    await this.loadConsentInfo()
  },

  methods: {
    async loadConsentInfo() {
      if (isEmpty(this.sessionId)) {
        this.hasError = true
        this.errorMessage = this.$t('auth.oauth.missing_session')
        this.isLoading = false
        return
      }

      if (!this.accessToken) {
        const redirect = `/oauth/consent?session=${this.sessionId}`
        return this.$router.push({
          name: 'access',
          query: { redirect },
        })
      }

      try {
        const { data } = await api.oauth.getConsentInfo(this.sessionId)
        this.clientName = data.clientName
        this.redirectUri = data.redirectUri
        this.isLoading = false

        this.getUserInfo().then(account => this.setUserInfo(account))
      } catch (error) {
        this.hasError = true
        this.errorMessage = error?.response?.data?.message || this.$t('auth.oauth.session_expired')
      } finally {
        this.isLoading = false
      }
    },

    async getUserInfo() {
      try {
        const { data } = await api.account.getMe()
        return data
      } catch (error) {
        return null
      }
    },

    setUserInfo(data) {
      if (!data) {
        return
      }

      this.userName = `${data.displayname ?? ''} ${data.familyname ?? ''}`.trim()
      this.userEmail = data.email ?? ''
    },

    async handleAuthorize() {
      this.isSubmitting = true

      try {
        const { data } = await api.oauth.submitConsent(
          this.sessionId,
          this.accessToken,
          'allow'
        )

        window.location.href = data.redirectUri
      } catch (error) {
        this.hasError = true
        this.errorMessage = error?.response?.data?.message || this.$t('auth.oauth.consent_error')
      } finally {
        this.isSubmitting = false
      }
    },

    async handleDeny() {
      this.isSubmitting = true

      try {
        const { data } = await api.oauth.submitConsent(
          this.sessionId,
          this.accessToken,
          'deny'
        )

        window.location.href = data.redirectUri
      } catch (error) {
        this.hasError = true
        this.errorMessage = error?.response?.data?.message || this.$t('auth.oauth.consent_error')
      } finally {
        this.isSubmitting = false
      }
    },

    handleLogout() {
      const redirect = `/oauth/consent?session=${this.sessionId}`
      return this.$router.push({ name: 'logout', query: { redirect } })
    },
  },
}
</script>

<style lang="scss" scoped>
.consentView-card {
  max-width: 450px;
  width: 450px;
}
</style>
